
import { useState, useEffect, useCallback } from "react";
import { OnboardingData } from "@/types/onboarding";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { debugLogger } from '@/utils/debugLogger';
import { calculateAge } from '@/utils/onboarding-utils';

const initialData: OnboardingData = {
  username: "",
  gender: "",
  orientation: "",
  role: "",
  relationshipType: "",
  sponsorshipValue: 0,
  birthdate: "",
  age: 0,
  bio: "",
  education: "",
  occupation: "",
  interests: [],
  location: null,
  geolocation: null,
  shareIncome: false,
  photos: [],
  height: "",
  bodyType: "",
  hairColor: "",
  eyeColor: "",
  smoker: "",
  drinker: "",
  piercings: "",
  tattoos: "",
  travelAvailability: "",
  maritalStatus: "",
  sigilo_presenca: "",
  children: "",
  aiDetectedTopics: []
};

export const useOnboardingData = () => {
  const { user } = useAuth();
  const [data, setData] = useState<OnboardingData>(initialData);
  const [loading, setLoading] = useState(false);
  const [lastSavedStep, setLastSavedStep] = useState(0);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean>(false);

  const loadOnboardingData = useCallback(async () => {
    if (!user) {
      debugLogger.warn('useOnboardingData: No user found, cannot load data');
      return;
    }

    setLoading(true);
    debugLogger.debug('useOnboardingData: Loading onboarding data for user:', user.id);

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        debugLogger.error('useOnboardingData: Error loading profile:', error);
        throw error;
      }

      if (profile) {
        debugLogger.debug('useOnboardingData: Profile loaded successfully:', {
          username: profile.username,
          onboarding_completed: profile.onboarding_completed,
          last_onboarding_step: profile.last_onboarding_step
        });

        // Set last saved step
        setLastSavedStep(profile.last_onboarding_step || 0);
        setOnboardingCompleted(profile.onboarding_completed || false);

        if (profile.onboarding_completed) {
          debugLogger.info('onboarding', 'User has already completed onboarding');
        }

        // Map profile data to onboarding data format with proper type casting
        const loadedData: OnboardingData = {
          username: profile.username || "",
          gender: (profile.gender || "") as OnboardingData['gender'],
          orientation: (profile.orientation || "") as OnboardingData['orientation'],
          role: (profile.role || "") as OnboardingData['role'],
          relationshipType: (profile.relationship_type || "") as OnboardingData['relationshipType'],
          sponsorshipValue: profile.sponsorship_value || 0,
          birthdate: profile.birth_date || "",
          age: calculateAge(profile.birth_date),
          bio: profile.bio || "",
          education: profile.education || "",
          occupation: profile.occupation || "",
          interests: profile.interests || [],
          location: profile.city && profile.state_code ? {
            city: profile.city,
            state: profile.state_code
          } : null,
          geolocation: profile.latitude && profile.longitude ? {
            latitude: profile.latitude,
            longitude: profile.longitude
          } : null,
          shareIncome: profile.share_income || false,
          photos: profile.profile_photos || [],
          height: profile.height || "",
          bodyType: profile.body_type || "",
          hairColor: profile.hair_color || "",
          eyeColor: profile.eye_color || "",
          smoker: profile.smoker || "",
          drinker: profile.drinker || "",
          piercings: profile.piercings || "",
          tattoos: profile.tattoos || "",
          travelAvailability: profile.travel_availability || "",
          maritalStatus: profile.marital_status || "",
          sigilo_presenca: profile.sigilo_presenca || "",
          children: profile.children || "",
          aiDetectedTopics: []
        };

        setData(loadedData);
        debugLogger.debug('useOnboardingData: Data mapped successfully');
      }
    } catch (error) {
      debugLogger.error('useOnboardingData: Exception loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updateData = useCallback((newData: Partial<OnboardingData>) => {
    debugLogger.debug('useOnboardingData: Updating data:', newData);
    
    setData(prev => {
      const updated = { ...prev, ...newData };
      
      // Automatically calculate age if birthdate is updated
      if (newData.birthdate !== undefined) {
        updated.age = calculateAge(newData.birthdate);
      }
      
      debugLogger.debug('useOnboardingData: Data updated successfully');
      return updated;
    });
  }, []);

  return {
    data,
    updateData,
    loading,
    setLoading,
    lastSavedStep,
    onboardingCompleted,
    loadOnboardingData
  };
};
